import { createFileRoute } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { SPECIALTIES, CITIES, INSURANCES, CONDITIONS } from "@/data/heallexa";
import { ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { specialtySlug, citySlug, stateFromNameSlug, stateNameSlug, toSlug } from "@/lib/seo-slugs";

export const Route = createFileRoute("/browse")({
 head: () => ({
 meta: [
 { title: "Browse Doctors, Dentists & Clinics by Specialty, Heallexa" },
 { name: "description", content: "Browse providers by specialty, city, insurance, and condition. Doctors, dentists, therapists, chiropractors, and urgent care on Heallexa." },
 { property: "og:title", content: "Browse Doctors, Dentists & Clinics by Specialty, Heallexa" },
 { property: "og:description", content: "Find healthcare providers by specialty, city, insurance, and condition on the Heallexa directory." },
 { property: "og:url", content: "https://heallexa.com/browse" },
 { property: "og:type", content: "website" },
 ],
 links: [{ rel: "canonical", href: "https://heallexa.com/browse" }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "CollectionPage",
 name: "Browse Doctors, Dentists & Clinics by Specialty",
 description: "Browse healthcare providers on Heallexa by specialty, city, insurance, and condition.",
 url: "https://heallexa.com/browse",
 isPartOf: { "@type": "WebSite", name: "Heallexa", url: "https://heallexa.com" },
 about: [
 { "@type": "Thing", name: "Specialties" },
 { "@type": "Thing", name: "Cities" },
 { "@type": "Thing", name: "Insurance" },
 { "@type": "Thing", name: "Conditions" },
 ],
 }),
 },
 ],
 }),
 component: BrowsePage,
});

function BrowsePage() {
 const { t } = useI18n();
 return (
 <div className="min-h-screen bg-background pb-1">
 <Navbar />
 <header className="pt-28 pb-12 border-b border-border bg-card">
 <div className="mx-auto max-w-6xl px-6">
 <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-foreground" style={{ letterSpacing: "-0.02em" }}>
 {t.browse.title}
 </h1>
 <p className="mt-3 text-muted-foreground max-w-2xl">{t.browse.subtitle}</p>
 </div>
 </header>

 <main className="mx-auto max-w-6xl px-6 py-16 space-y-16">
 <Section title={t.browse.bySpec}>
 {SPECIALTIES.map((s) => (
 <Tile key={s} href={`/specialties/${specialtySlug(s)}`}>{s}</Tile>
 ))}
 </Section>

 <Section title={t.browse.byCity}>
 {CITIES.map((c) => {
 const [cityPart, stateCode] = c.split(", ");
 const stateRec = stateFromNameSlug(stateCode ?? "");
 const stSlug = stateRec ? stateNameSlug(stateRec.name) : (stateCode ?? "").toLowerCase();
 return <Tile key={c} href={`/locations/${stSlug}/${citySlug(c)}`}>{c}</Tile>;
 })}
 </Section>

 <Section title={t.browse.byIns}>
 {INSURANCES.map((i) => (
 <Tile key={i.id} href={`/insurance/${i.id}`} accent={i.color}>{i.short}</Tile>
 ))}
 </Section>

 <Section title={t.browse.byCond}>
 {CONDITIONS.map((c) => (
 <Tile key={c} href={`/conditions/${toSlug(c)}`}>{c}</Tile>
 ))}
 </Section>
 </main>

 <FooterSection />
 </div>
 );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
 return (
 <section>
 <h2 className="text-2xl font-semibold text-foreground mb-6 tracking-tight">{title}</h2>
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">{children}</div>
 </section>
 );
}

function Tile({ href, children, accent }: { href: string; children: React.ReactNode; accent?: string }) {
 return (
 <a
 href={href}
 className="group flex items-center justify-between border border-border bg-card px-4 py-4 hover:border-primary hover:bg-accent transition-colors"
 style={accent ? { borderLeft: `3px solid ${accent}` } : undefined}
 >
 <span className="text-sm font-medium text-foreground">{children}</span>
 <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
 </a>
 );
}
